/* eslint-disable no-nested-ternary */

import Joi from 'joi-browser';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  Delete,
  MailOutline,
  NoAccounts,
  PersonOff,
} from '@mui/icons-material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Email from '@mui/icons-material/Email';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Key from '@mui/icons-material/Key';
import PersonIcon from '@mui/icons-material/Person';
import Phone from '@mui/icons-material/Phone';
import LoadingButton from '@mui/lab/LoadingButton';
import { red } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';

import { useDropzone } from 'react-dropzone';

import {
  addBarnUserFile,
  deleteUser,
  getBarnUserFile,
  updateUser,
} from '../../../../../redux/user/actions';

import BarnHorsesAutocomplete from '../../../../../components/Autocompletes/BarnHorses';
import Horse from '../../../../../components/Horse';
import QuestriUserService from '../../../../../services/QuestriUserService';
import {
  blueGrey,
  complementary,
} from '../../../../../utils/brandColors';
import { getFileIcon } from '../../../../../utils/fileIcons';
import { normalizePhoneNumber } from '../../../../../utils/phone';
import { formatStringOfRolesIntoArray } from '../../../../../utils/rolesHelper';
import theme from '../../../../../utils/theme';
import AddEditModal from '../../../../horse/Horse/components/HorseAccessCard/components/AddEditModal';
import AboutUserRolesModal from '../AboutUserRolesModal';
import UserRoles from '../UserRoles';
import styles from './styles';

const useStyles = makeStyles(styles);
const isImage = /.png|jpeg|jpg|gif$/;

const DEFAULT_VALIDATION = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  emergencyContactPhone: '',
  emergencyContactPhoneBackup: '',
}

export const UserProfileComponent = (props) => {
  const { mode } = useColorScheme();
  const {
    tenant,
    tenantUser,
    open,
    onClose,
    initialTab = 'profile',
  } = props;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const classes = useStyles();

  const user = useSelector((state) => state.user.user);
  const { barnUsers } = useSelector((state) => state.barn);
  const lastActivityFriendly = tenantUser && tenantUser.lastActivityTimestamp
    ? moment(tenantUser.lastActivityTimestamp).fromNow()
    : 'Never logged in';
  const createdFriendly = tenantUser && tenantUser.createdTimestamp
    ? moment(tenantUser.createdTimestamp).format('ll')
    : 'Unknown';

  const [openAboutRolesModal, setOpenAboutRolesModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveAccount, setConfirmRemoveAccount] = useState(false);
  const [loadingHorseAccess, setLoadingHorseAccess] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseAccess, setSelectedHorseAccess] = React.useState(null);
  const [horseAccess, setHorseAccess] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openHorsePicker, setOpenHorsePicker] = useState(false);
  const [pendingHorseSelection, setPendingHorseSelection] = useState(null);
  const [inlineRoles, setInlineRoles] = useState(formatStringOfRolesIntoArray((tenantUser && tenantUser.roles) || ''));

  // Form Fields
  const [validation, setValidation] = useState(DEFAULT_VALIDATION);
  const [formBanner, setFormBanner] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddressLine1, setEditAddressLine1] = useState('');
  const [editAddressLine2, setEditAddressLine2] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editStateProvince, setEditStateProvince] = useState('');
  const [editZipPostal, setEditZipPostal] = useState('');
  const [editEmergencyContact, setEditEmergencyContact] = useState({});
  const [editReferenceNumbers, setEditReferenceNumbers] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const formatRoleLabel = (role) => role.charAt(0).toUpperCase() + role.slice(1);
  const globalAccessRoles = ['owner', 'admin', 'staff'];
  const userGlobalAccessBadges = inlineRoles.filter((role) => globalAccessRoles.indexOf(role) >= 0);
  const hasGlobalHorseAccess = userGlobalAccessBadges.length > 0;
  const renderAttachmentIcon = (fileName) => (
    downloadingFile === fileName
      ? <CircularProgress size={18} />
      : getFileIcon(fileName)
  );

  const {
    getRootProps: getAttachmentDropProps,
    getInputProps: getAttachmentInputProps,
    isDragActive: isAttachmentDragActive,
    fileRejections: attachmentFileRejections,
    open: openAttachmentPicker,
  } = useDropzone({
    onDropAccepted: (newFiles) => setAttachments((prev) => [...prev, ...newFiles]),
    disabled: deleting || saving,
    noClick: true,
    noKeyboard: true,
  });
  const attachmentDropBackground = isAttachmentDragActive
    ? mode === 'light'
      ? blueGrey[100]
      : blueGrey[700]
    : mode === 'light'
      ? blueGrey[50]
      : blueGrey[800];
  const attachmentDropBorder = isAttachmentDragActive
    ? blueGrey[400]
    : blueGrey[200];
  const accessTypeColor = (type) => {
    const map = {
      Owner: 'success',
      Staff: 'info',
      Admin: 'warning',
      Trainer: 'secondary',
      Veterinarian: 'secondary',
      Farrier: 'secondary',
    };
    return map[type] || 'default';
  };

  const handleDownload = (file) => {
    setDownloadingFile(file);
    dispatch(getBarnUserFile(tenantUser.id, file, true)).then(() => {
      setDownloadingFile(null);
    }).catch((error) => {
      console.error(error);
      setDownloadingFile(null);
    });
  };

  React.useEffect(() => {
    if (tenantUser) {
      setFormBanner('')
      setHorseAccess([]);
      setDeleting(false);
      setSaving(false);
      setConfirmDelete(false);
      setConfirmRemoveAccount(false);
      setValidation(DEFAULT_VALIDATION);
      setEditFirstName(tenantUser.firstName || '');
      setEditLastName(tenantUser.lastName || '');
      setEditEmail(tenantUser.email || '');
      setEditPhone(tenantUser.phone || '');
      setEditAddressLine1(tenantUser.addressLine1 || '');
      setEditAddressLine2(tenantUser.addressLine2 || '');
      setEditCity(tenantUser.city || '');
      setEditStateProvince(tenantUser.stateProvinceCounty || '');
      setEditZipPostal(tenantUser.zipOrPostalcode || '');
      setEditEmergencyContact(tenantUser.emergencyContact || {});
      setEditReferenceNumbers(tenantUser.referenceNumbers || {});
      setAttachments([]);
      setAttachmentsToRemove([])
      setInlineRoles(formatStringOfRolesIntoArray((tenantUser.roles) || ''));
      setLoadingHorseAccess(true);
      QuestriUserService.getUserHorseAccess(tenantUser.id).then((data) => {
        setHorseAccess(data);
        setLoadingHorseAccess(false);
      }).catch((error) => {
        console.error(error);
        setLoadingHorseAccess(false);
      })
    }
  }, [tenantUser]);

  const handleRemoveAccount = () => {
    setDeleting(true);
    dispatch(updateUser(
      tenantUser.id,
      {
        removeAuth0Account: true,
        invited: false,
      },
      false,
    )).then(() => {
      setDeleting(false);
      onClose(true);
    }).catch(() => {
      setDeleting(false);
    })
  }

  const scrollToField = (fieldId) => {
    if (!fieldId) {
      return;
    }
    const target = document.getElementById(fieldId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      if (typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }
    }
  };

  const addAttachmetnsAndPatchUser = (updatedUser) => {
    const allRequests = [];
    for (let i = 0; i < attachments.length; i += 1) {
      allRequests.push(dispatch(addBarnUserFile(updatedUser.id, attachments[i])));
    }

    // once all done, process
    Promise.all(allRequests).then((values) => {
      // eslint-disable-next-line no-console
      dispatch(updateUser(tenantUser.id, { attachments: [...((updatedUser.attachments) || []), ...values] }, false)).then(() => {
        setSaving(false);
        onClose(tenantUser);
      }).catch((error) => {
        setSaving(false);
        console.error(error);
      });
    });
  }

  const handleSaveName = () => {
    const newValidation = { ...validation };
    const setFieldError = (field, message, fieldId) => {
      if (field) {
        newValidation[field] = message;
      }
      setValidation({ ...newValidation });
      setFormBanner(message);
      scrollToField(fieldId);
    };

    if (editEmail && Joi.validate(editEmail, Joi.string().email()).error) {
      setFieldError('email', 'Enter a valid email address before saving.', 'email');
      return;
    }
    newValidation.email = '';

    const normalizedPhone = normalizePhoneNumber(editPhone);
    if (!normalizedPhone.isValid) {
      setFieldError('phone', 'Please enter a valid phone number.', 'phone');
      return;
    }
    newValidation.phone = '';
    const emergencyContact = editEmergencyContact || {};
    const normalizedEmergencyPhone = normalizePhoneNumber(emergencyContact.phone);
    if (!normalizedEmergencyPhone.isValid) {
      setFieldError('emergencyContactPhone', 'Please enter a valid phone number.', 'emergency-contact-phone');
      return;
    }
    newValidation.emergencyContactPhone = '';
    const normalizedEmergencyPhoneBackup = normalizePhoneNumber(emergencyContact.phoneBackup);
    if (!normalizedEmergencyPhoneBackup.isValid) {
      setFieldError('emergencyContactPhoneBackup', 'Please enter a valid phone number.', 'emergency-contact-phone-2');
      return;
    }
    newValidation.emergencyContactPhoneBackup = '';
    if (editFirstName.length < 2 || editFirstName.length > 50) {
      setFieldError('firstName', 'First name should be between 2 and 50 characters.', 'first-name');
      return;
    }
    newValidation.firstName = '';
    if (editLastName.length < 2 || editLastName.length > 50) {
      setFieldError('lastName', 'Last name should be between 2 and 50 characters.', 'last-name');
      return;
    }
    newValidation.lastName = '';

    if (editEmail && !tenantUser.auth0UserId
      && !!barnUsers.find((e) => e.id !== tenantUser.id && e.email
        && e.email.toLowerCase() === editEmail.toLowerCase())) {
      setFieldError(
        'email',
        'A user with this email already exists in your organization.',
        'email',
      );
      return;
    }
    setValidation({ ...newValidation });
    setFormBanner('');
    setSaving(true);
    const formattedPhone = normalizedPhone.e164 || null;
    const formattedEmergencyContact = {
      ...emergencyContact,
      phone: normalizedEmergencyPhone.e164 || null,
      phoneBackup: normalizedEmergencyPhoneBackup.e164 || null,
    };
    let updatedAttachmentsList = null;
    if (attachmentsToRemove && attachmentsToRemove.length > 0 && tenantUser.attachments && tenantUser.attachments.length > 0) {
      // remove any removed files
      updatedAttachmentsList = tenantUser.attachments.filter((f) => attachmentsToRemove.indexOf(f) < 0);
    }
    dispatch(updateUser(tenantUser.id, {
      email: editEmail || null,
      firstName: editFirstName,
      lastName: editLastName,
      phone: formattedPhone,
      addressLine1: editAddressLine1 || null,
      addressLine2: editAddressLine2 || null,
      city: editCity || null,
      zipOrPostalcode: editZipPostal || null,
      stateProvinceCounty: editStateProvince || null,
      emergencyContact: formattedEmergencyContact,
      referenceNumbers: editReferenceNumbers,
      attachments: updatedAttachmentsList || null,
    }, false)).then((updatedUser) => {
      if (attachments.length > 0) {
        // upload files then patch note with files
        addAttachmetnsAndPatchUser(updatedUser);
      } else {
        setSaving(false);
        onClose(tenantUser);
      }
    }).catch(() => {
      setSaving(false);
    })
  };

  const handleDeleteUser = () => {
    setDeleting(true);
    dispatch(deleteUser(
      tenantUser.id,
    )).then(() => {
      setDeleting(false);
      onClose(true);
    }).catch(() => {
      setDeleting(false);
    });
  }

  const onlyOneOwner = barnUsers.filter((bu) => bu.roles.indexOf('owner') >= 0).length <= 1;
  const canDeleteUser = !!(user && tenantUser && user.auth0UserId !== tenantUser.auth0UserId);
  const canRemoveAccountAccess = !!(user && tenantUser && tenantUser.auth0UserId && user.auth0UserId && user.auth0UserId !== tenantUser.auth0UserId);
  const phoneError = !!validation.phone;
  const emergencyPhoneError = !!validation.emergencyContactPhone;
  const emergencyPhoneBackupError = !!validation.emergencyContactPhoneBackup;
  const normalizedPrimaryPhone = normalizePhoneNumber(editPhone);
  const normalizedEmergencyPhone = normalizePhoneNumber(editEmergencyContact.phone);
  const normalizedEmergencyPhoneBackup = normalizePhoneNumber(editEmergencyContact.phoneBackup);
  const isPrimaryPhoneValid = normalizedPrimaryPhone.isValid;
  const isEmergencyPhoneValid = normalizedEmergencyPhone.isValid;
  const isEmergencyPhoneBackupValid = normalizedEmergencyPhoneBackup.isValid;
  const hasValidationErrors = Object.values(validation).some((value) => !!value);
  const isSaveDisabled = deleting
    || saving
    || hasValidationErrors
    || !isPrimaryPhoneValid
    || !isEmergencyPhoneValid
    || !isEmergencyPhoneBackupValid;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="user-role-info"
      aria-describedby="user-role-info"
      id="user-role-info-dialog"
      className={classes.root}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="remove-user-title" sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: mode === 'light'
              ? '#fff'
              : blueGrey[900],
            borderBottom: `1px solid ${blueGrey[100]}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              px: 3,
              py: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Avatar
                src={tenantUser && tenantUser.avatar}
                alt={tenantUser && tenantUser.userFullname}
                sx={{
                  width: 56,
                  height: 56,
                  mr: 2,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="span" noWrap>{tenantUser && tenantUser.userFullName}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                  {`Created ${createdFriendly}`}
                  {tenantUser && tenantUser.createdByName && ` • by ${tenantUser.createdByName}`}
                </Typography>
                {
                  tenantUser && tenantUser.auth0UserId && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Tooltip title="This user has an account and can log in.">
                        <Chip
                          label="Active Account"
                          color="success"
                          sx={{
                            alignSelf: 'flex-start',
                            backgroundColor: complementary[500],
                            color: '#fff !important',
                            '& .MuiChip-label': { color: '#fff !important' },
                            '& .MuiChip-icon': { color: '#fff !important' },
                          }}
                          icon={<CheckCircle />}
                        />
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        Last activity: {lastActivityFriendly}
                      </Typography>
                    </Box>
                  )
                }
                {
                  tenantUser && tenantUser.email && tenantUser.invited && !tenantUser.auth0UserId && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Tooltip title="This user has been invited to join your Questri organizatin but has not yet created an account.">
                        <Chip
                          label="Invite Pending"
                          color="info"
                          variant="outlined"
                          icon={<MailOutline />}
                        />
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() => {
                          setSaving(true);
                          dispatch(updateUser(tenantUser.id, { invited: false }, false)).then(() => {
                            setSaving(false);
                            onClose(true);
                          }).catch(() => {
                            setSaving(false);
                            setFormBanner('Invitation revoke failed. Please contact Questri support if the issue persists.');
                          })
                        }}
                        startIcon={<Cancel />}
                      >Revoke Invitation
                      </Button>
                    </Box>
                  )
                }
                {
                  tenantUser && tenantUser.email && !tenantUser.invited && !tenantUser.auth0UserId && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Tooltip title="This user has not been invited to join your Questri organization.">
                        <Chip
                          label="No Account"
                          icon={<PersonOff />}
                        />
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() => {
                          setSaving(true);
                          dispatch(updateUser(tenantUser.id, {
                            invited: true,
                            email: tenantUser.email,
                          }, false)).then(() => {
                            setSaving(false);
                            onClose(true);
                          }).catch(() => {
                            setSaving(false);
                            setFormBanner('Invitation failed. Please contact Questri support if the issue persists.');
                          })
                        }}
                        startIcon={<MailOutline />}
                      >Invite User
                      </Button>
                    </Box>
                  )
                }

                {
                  tenantUser && !tenantUser.email && !tenantUser.auth0UserId && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box>
                        <Tooltip title="This user has no email has not been invited to join your Questri organization.">
                          <Chip
                            label="No Account"
                            icon={<PersonOff />}
                          />
                        </Tooltip>
                        <Box />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        To invite this user to your Questri organization, first add an email for the user.
                      </Typography>
                    </Box>
                  )
                }
                {
                  tenantUser && (
                    <UserRoles
                      tenant={tenant}
                      tenantUser={{
                        ...tenantUser,
                        roles: `[${inlineRoles.join(',')}]`,
                      }}
                      onlyOneOwner={onlyOneOwner}
                      isLoading={false}
                      onRolesUpdate={(rolesArray) => setInlineRoles(rolesArray)}
                      infoButton={(
                        <Tooltip title="About User Roles">
                          <IconButton onClick={() => setOpenAboutRolesModal(true)}>
                            <InfoOutlined />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                  )
                }
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexShrink: 0,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={onClose}
                disabled={deleting}
                id="cancel-edit-button"
              >
                Cancel
              </Button>
              <Tooltip
                title={saving
                  ? 'Saving changes…'
                  : 'Save profile changes'}
              >
                <span>
                  <LoadingButton
                    disabled={isSaveDisabled}
                    loading={saving}
                    onClick={handleSaveName}
                    color="primary"
                    variant="contained"
                    autoFocus
                    id="confirm-edit-button"
                  >
                    Save
                  </LoadingButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ px: 3 }}
          >
            <Tab value="profile" label="Profile" />
            <Tab value="emergency" label="Emergency" />
            <Tab value="files" label="Files & IDs" />
            <Tab value="horse-access" label="Horse Access" />
            <Tab value="advanced" label="Advanced" />
          </Tabs>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {formBanner && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formBanner}
          </Alert>
        )}

        {activeTab === 'profile' && (
          <>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
              }}
            >
              <Typography sx={{ ml: 2 }}>Contact Information</Typography>
            </Box>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': {
                  m: 1,
                  width: '23ch',
                },
              }}
            >
              <TextField
                error={!!validation.firstName}
                margin="normal"
                variant="outlined"
                id="first-name"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                label="First Name"
                helperText={validation.firstName
                  ? validation.firstName
                  : 'Required'}
              />
              <TextField
                error={!!validation.lastName}
                margin="normal"
                variant="outlined"
                id="last-name"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                label="Last Name"
                helperText={validation.lastName
                  ? validation.lastName
                  : 'Required'}
              />
              <Tooltip title={tenantUser && tenantUser.auth0UserId && 'This user already has an account created under this email. Email cannot be modified.'}>
                <TextField
                  error={!!validation.email}
                  margin="normal"
                  variant="outlined"
                  id="email"
                  disabled={!!(tenantUser && tenantUser.auth0UserId)}
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  label="Email"
                  helperText={validation.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
              <TextField
                error={phoneError}
                margin="normal"
                variant="outlined"
                id="phone"
                value={editPhone}
                onChange={(e) => {
                  setEditPhone(e.target.value);
                  setValidation({
                    ...validation,
                    phone: '',
                  });
                  setFormBanner('');
                }}
                onBlur={() => {
                  const normalized = normalizePhoneNumber(editPhone);
                  if (!normalized.isValid && editPhone) {
                    setValidation({
                      ...validation,
                      phone: 'Please enter a valid phone number.',
                    });
                  }
                }}
                label="Phone"
                helperText={validation.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
              }}
            >
              <Typography sx={{ ml: 2 }}>Address</Typography>
            </Box>
            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1 } }}
            >
              <TextField
                margin="normal"
                variant="outlined"
                id="addresss-1"
                value={editAddressLine1}
                onChange={(e) => setEditAddressLine1(e.target.value)}
                label="Address"
                sx={{ width: '30ch ' }}
              />
              <TextField
                margin="normal"
                variant="outlined"
                id="addresss-2"
                value={editAddressLine2}
                onChange={(e) => setEditAddressLine2(e.target.value)}
                label="Apartment, Suite, etc."
                sx={{ width: '15ch ' }}
              />
              <TextField
                margin="normal"
                variant="outlined"
                id="zip-postal"
                value={editZipPostal}
                onChange={(e) => setEditZipPostal(e.target.value)}
                label="Zip/Postal Code"
                sx={{ width: '15ch ' }}
                type="number"
              />
              <TextField
                margin="normal"
                variant="outlined"
                id="city"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                label="City"
                sx={{ width: '23ch ' }}
              />
              <TextField
                margin="normal"
                variant="outlined"
                id="state-province"
                value={editStateProvince}
                onChange={(e) => setEditStateProvince(e.target.value)}
                label="State/Province"
                sx={{ width: '23ch ' }}
              />
            </Box>
          </>
        )}

        {activeTab === 'emergency' && (
          <>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
              }}
            >
              <Typography sx={{ ml: 2 }}>Emergency Contacts</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    border: `1px solid ${blueGrey[100]}`,
                    borderRadius: 2,
                    p: 2,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2">Primary Contact</Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    id="emergency-contact-1"
                    value={editEmergencyContact.name}
                    onChange={(e) => setEditEmergencyContact({
                      ...editEmergencyContact,
                      name: e.target.value,
                    })}
                    label="Name"
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    id="emergency-contact-phone"
                    value={editEmergencyContact.phone}
                    onChange={(e) => {
                      setEditEmergencyContact({
                        ...editEmergencyContact,
                        phone: e.target.value,
                      });
                      setValidation({
                        ...validation,
                        emergencyContactPhone: '',
                      });
                      setFormBanner('');
                    }}
                    onBlur={() => {
                      const normalized = normalizePhoneNumber(editEmergencyContact.phone);
                      if (!normalized.isValid && editEmergencyContact.phone) {
                        setValidation({
                          ...validation,
                          emergencyContactPhone: 'Please enter a valid phone number.',
                        });
                      }
                    }}
                    label="Phone"
                    error={emergencyPhoneError}
                    helperText={validation.emergencyContactPhone}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    border: `1px solid ${blueGrey[100]}`,
                    borderRadius: 2,
                    p: 2,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2">Backup Contact</Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    id="emergency-contact-2"
                    value={editEmergencyContact.nameBackup}
                    onChange={(e) => setEditEmergencyContact({
                      ...editEmergencyContact,
                      nameBackup: e.target.value,
                    })}
                    label="Name"
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    id="emergency-contact-phone-2"
                    value={editEmergencyContact.phoneBackup}
                    onChange={(e) => {
                      setEditEmergencyContact({
                        ...editEmergencyContact,
                        phoneBackup: e.target.value,
                      });
                      setValidation({
                        ...validation,
                        emergencyContactPhoneBackup: '',
                      });
                      setFormBanner('');
                    }}
                    onBlur={() => {
                      const normalized = normalizePhoneNumber(editEmergencyContact.phoneBackup);
                      if (!normalized.isValid && editEmergencyContact.phoneBackup) {
                        setValidation({
                          ...validation,
                          emergencyContactPhoneBackup: 'Please enter a valid phone number.',
                        });
                      }
                    }}
                    label="Phone"
                    error={emergencyPhoneBackupError}
                    helperText={validation.emergencyContactPhoneBackup}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                border: `1px solid ${blueGrey[200]}`,
                borderRadius: 2,
                p: 2,
                mt: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Special Instructions</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add allergy info, medications, or other notes staff should know.
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                id="emergency-comment"
                multiline
                rows={3}
                value={editEmergencyContact.comments}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  comments: e.target.value,
                })}
              />
            </Box>
          </>
        )}

        {activeTab === 'files' && (
          <>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography sx={{ ml: 2 }}>IDs & Reference Numbers</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  USEF, FEI, USHJA, coach credentials, etc.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setEditReferenceNumbers({
                    ...editReferenceNumbers,
                    'NEW ID': '',
                  });
                }}
                startIcon={<Add fontSize="small" />}
                sx={{ mr: 2 }}
              >
                Add New
              </Button>
            </Box>
            <Box>
              {
                Object.keys(editReferenceNumbers).map((num) => (
                  <Box
                    key={`ref-number-${num}`}
                    component="form"
                    sx={{
                      '& .MuiTextField-root': { m: 1 },
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      margin="normal"
                      size="small"
                      variant="outlined"
                      id={`ref-num-key-${num}`}
                      defaultValue={num}
                      onBlur={(e) => {
                        const deepCopy = JSON.parse(JSON.stringify(editReferenceNumbers));
                        const currentVal = JSON.parse(JSON.stringify(editReferenceNumbers[num]));
                        delete deepCopy[num];

                        setEditReferenceNumbers({
                          ...deepCopy,
                          [e.target.value]: currentVal,
                        })
                      }}
                      label="ID or Name"
                      sx={{ width: '23ch ' }}
                    />
                    <TextField
                      margin="normal"
                      size="small"
                      variant="outlined"
                      id={`ref-num-val-${num}`}
                      value={editReferenceNumbers[num]}
                      onChange={(e) => setEditReferenceNumbers({
                        ...editReferenceNumbers,
                        [num]: e.target.value,
                      })}
                      label="Value"
                      sx={{ width: '23ch ' }}
                    />
                    <Tooltip title="Remove ID">
                      <IconButton
                        onClick={() => {
                          const deepCopy = JSON.parse(JSON.stringify(editReferenceNumbers));
                          delete deepCopy[num];
                          setEditReferenceNumbers(deepCopy);

                          if (!Object.keys(deepCopy).length) {
                            setEditReferenceNumbers({ 'NEW ID': '' });
                          }
                        }}
                        size="small"
                        aria-label="Remove ID"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))
              }
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
              }}
            >
              <Typography sx={{ ml: 2 }}>File Attachments</Typography>
            </Box>

            <Grid container spacing={2} sx={{ p: 1 }}>
              <Grid item xs={12}>
                <Box>
                  <Box
                    {...getAttachmentDropProps()}
                    sx={{
                      border: `2px dashed ${blueGrey[200]}`,
                      borderRadius: 2,
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                      backgroundColor: attachmentDropBackground,
                      transition: 'background-color 120ms ease, border-color 120ms ease',
                      borderColor: attachmentDropBorder,
                      minHeight: 140,
                    }}
                  >
                    <input {...getAttachmentInputProps()} />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 220,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>Attach files to this user</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Drag & drop PDFs, images, or documents anywhere in this box, or use the button to browse.
                      </Typography>
                      {attachmentFileRejections.length > 0 && (
                        <Typography variant="caption" color="error">
                          {attachmentFileRejections[0]?.errors[0]?.message}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={openAttachmentPicker}
                        disabled={deleting || saving}
                        size="small"
                      >
                        Upload
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Pending uploads (added after you save):
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {
                      attachments.length > 0
                        ? attachments.map((file, index) => (
                          <Chip
                            key={`pending-attachment-${file.name}-${file.lastModified || file.size || index}`}
                            label={file.name}
                            icon={getFileIcon(file.name)}
                            variant="outlined"
                            size="small"
                            onDelete={() => {
                              const updated = [...attachments];
                              updated.splice(index, 1);
                              setAttachments(updated);
                            }}
                          />
                        ))
                        : (
                          <Typography variant="caption" color="text.disabled">
                            No pending uploads yet.
                          </Typography>
                        )
                    }
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {
                    tenantUser && tenantUser.attachments
                    && tenantUser.attachments.filter((file) => attachmentsToRemove.indexOf(file) < 0).map((file) => (
                      <Box key={`file-${file}`}>
                        <Tooltip title="Click to download">
                          <Chip
                            onClick={() => {
                              handleDownload(file, file.match(isImage))
                            }}
                            icon={renderAttachmentIcon(file)}
                            variant="outlined"
                            sx={{ maxWidth: 240 }}
                            label={file}
                            deleteIcon={<Tooltip title="Remove attachment from user"><Delete /></Tooltip>}
                            onDelete={() => { setAttachmentsToRemove([...attachmentsToRemove, file]) }}
                          />
                        </Tooltip>
                      </Box>
                    ))
                  }
                  {
                    tenantUser && (!tenantUser.attachments
                      || tenantUser.attachments.filter((file) => attachmentsToRemove.indexOf(file) < 0).length === 0)
                    && (
                      <Typography
                        sx={{
                          mb: 1,
                          fontWeight: 300,
                        }}
                      >No File Attachments yet.
                      </Typography>
                    )
                  }
                </Box>
              </Grid>
            </Grid>
          </>
        )}

        {activeTab === 'horse-access' && (
          <>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: mode === 'light'
                  ? blueGrey[50]
                  : blueGrey[800],
                p: 0.5,
                mt: 2,
                mb: 1,
              }}
            >
              <Typography sx={{ ml: 2 }}>Horse Access</Typography>
            </Box>
            {
              loadingHorseAccess && (
                <Box sx={{ m: 1 }}>
                  <Skeleton variant="rounded" width={450} height={40} sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" width={450} height={40} />
                </Box>
              )
            }
            {
              !loadingHorseAccess && (

                <Box sx={{ m: 1 }}>
                  {
                    hasGlobalHorseAccess
                      ? (
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${blueGrey[100]}`,
                            backgroundColor: mode === 'light'
                              ? blueGrey[50]
                              : blueGrey[800],
                            p: 2,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                            Full Barn Access
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {`Because this user holds ${userGlobalAccessBadges.map((role) => formatRoleLabel(role)).join(', ')} permissions, they automatically have access to every horse.`}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {
                              userGlobalAccessBadges.map((role) => (
                                <Chip
                                  key={`global-access-${role}`}
                                  size="small"
                                  color="primary"
                                  icon={<Key fontSize="small" />}
                                  label={`${formatRoleLabel(role)} role`}
                                />
                              ))
                            }
                          </Stack>
                        </Box>
                      )
                      : (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                              flexWrap: 'wrap',
                              gap: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={600}>Assigned Horses</Typography>
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => {
                                setPendingHorseSelection(null);
                                setOpenHorsePicker(true);
                              }}
                              size="small"
                            >
                              Add Horse Access
                            </Button>
                          </Box>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left">Horse</TableCell>
                                  <TableCell align="left">Access Type</TableCell>
                                  <TableCell align="left">Note</TableCell>
                                  <TableCell align="left">Ownership</TableCell>
                                  <TableCell align="left">Access Start</TableCell>
                                  <TableCell align="left">Access End</TableCell>
                                  <TableCell
                                    align="center"
                                    style={{
                                      maxWidth: 90,
                                      minWidth: 90,
                                    }}
                                  >Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {
                                horseAccess.length === 0
                                  ? (
                                    <TableRow>
                                      <TableCell colSpan={7}>
                                        <Typography sx={{ fontWeight: 300 }}>No horse-specific access granted yet.</Typography>
                                      </TableCell>
                                    </TableRow>
                                  )
                                  : (
                                    horseAccess.map((ha) => (
                                      <TableRow key={ha.id}>
                                        <TableCell align="left"><Horse horse={ha.horse} /></TableCell>
                                        <TableCell align="left">
                                          <Chip
                                            size="small"
                                            label={ha.type || 'N/A'}
                                            color={accessTypeColor(ha.type)}
                                          />
                                        </TableCell>
                                        <TableCell align="left">
                                          {ha.accessNote
                                            ? <Typography variant="caption" component="div">{`"${ha.accessNote}"`}</Typography>
                                            : <Typography variant="caption" color="text.secondary">No notes</Typography>}
                                        </TableCell>
                                        <TableCell align="left">
                                          {ha.owner
                                            ? (
                                              <Chip
                                                size="small"
                                                color="success"
                                                label={`${ha.ownershipPercentage || 0}%`}
                                                sx={{ mr: 0.5 }}
                                              />
                                            )
                                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                                          {ha.owner && ha.ownershipNotes && <Typography variant="caption" component="div">{`"${ha.ownershipNotes}"`}</Typography>}
                                        </TableCell>
                                        <TableCell align="left">{moment(ha.startDate).format('ll')}</TableCell>
                                        <TableCell align="left">
                                          {ha.endDate
                                            ? moment(ha.endDate).format('ll')
                                            : 'Active'}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          style={{
                                            maxWidth: 90,
                                            minWidth: 90,
                                          }}
                                        >
                                          <IconButton
                                            style={{ padding: 4 }}
                                            onClick={() => {
                                              setSelectedHorseAccess(ha);
                                              setOpenAddEditModal(true);
                                            }}
                                          >
                                            <Edit />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )
                              }
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )
                  }
                </Box>
              )
            }
          </>
        )}

        {activeTab === 'advanced' && (
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${red[100]}`,
              backgroundColor: red[50],
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            <Box>
              <Typography variant="h6" color="error">Danger Zone</Typography>
              <Typography variant="body2" color="text.secondary">
                Destructive actions cannot be undone. Please double-check before continuing.
              </Typography>
            </Box>

            <Stack spacing={2}>
              {
                (canRemoveAccountAccess && !confirmDelete) && (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${red[100]}`,
                      backgroundColor: '#fff',
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600}>Remove Account/Login Access</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Keep the user record but revoke their ability to log into Questri.
                    </Typography>
                    <LoadingButton
                      disabled={deleting || saving}
                      loading={deleting}
                      onClick={() => {
                        if (!confirmRemoveAccount) {
                          setConfirmRemoveAccount(true);
                        } else {
                          handleRemoveAccount();
                        }
                      }}
                      color="warning"
                      startIcon={<NoAccounts />}
                      variant={confirmRemoveAccount
                        ? 'contained'
                        : 'outlined'}
                      id="remove-button"
                    >
                      {!confirmRemoveAccount
                        ? 'Remove Account/Login Access'
                        : 'Confirm Remove Account'}
                    </LoadingButton>
                  </Box>
                )
              }

              {
                (canDeleteUser && !confirmRemoveAccount) && (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${red[100]}`,
                      backgroundColor: '#fff',
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600}>Delete User</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Permanently remove this user from your organization and revoke all access.
                    </Typography>
                    <LoadingButton
                      disabled={deleting || saving}
                      loading={deleting}
                      onClick={() => {
                        if (!confirmDelete) {
                          setConfirmDelete(true);
                        } else {
                          handleDeleteUser();
                        }
                      }}
                      color="error"
                      startIcon={<Close />}
                      variant={confirmDelete
                        ? 'contained'
                        : 'outlined'}
                      id="delete-button"
                    >
                      {!confirmDelete
                        ? 'Delete User'
                        : 'Confirm Delete User'}
                    </LoadingButton>
                  </Box>
                )
              }

              {!canDeleteUser && !canRemoveAccountAccess && (
                <Typography variant="body2" color="text.secondary">
                  No destructive actions are available for this user.
                </Typography>
              )}
            </Stack>

            {confirmRemoveAccount && (
              <Alert severity="warning">
                Are you sure you want to remove the account
                <b> {tenantUser && tenantUser.userFullName}</b>? The account will no longer be able to login to Questri,
                but the user and data will remain.
              </Alert>
            )}
            {confirmDelete && (
              <Alert severity="warning">
                Are you sure you want to remove the user
                <b> {tenantUser && tenantUser.userFullName}</b> from this organization?
                Any billing transactions or invoices associated with this user will lose the association. This action cannot be undone.
              </Alert>
            )}
          </Box>
        )}
        <Divider />
      </DialogContent>

      <AboutUserRolesModal
        open={openAboutRolesModal}
        onClose={() => setOpenAboutRolesModal(false)}
      />
      {
        openAddEditModal && (
          <AddEditModal
            open={openAddEditModal}
            horseAccess={selectedHorseAccess}
            horseId={selectedHorseAccess && selectedHorseAccess.horse.id}
            user={tenantUser}
            onClose={() => {
              setLoadingHorseAccess(true);
              setOpenAddEditModal(false);
              QuestriUserService.getUserHorseAccess(tenantUser.id).then((data) => {
                setLoadingHorseAccess(false);
                setHorseAccess(data);
              }).catch((error) => {
                console.error(error);
                setLoadingHorseAccess(false);
              })
            }}
          />
        )
      }
      <Dialog
        open={openHorsePicker}
        onClose={() => {
          setOpenHorsePicker(false);
          setPendingHorseSelection(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Horse</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose a horse to grant access to {tenantUser && tenantUser.userFullName}.
          </Typography>
          <BarnHorsesAutocomplete
            multiple={false}
            limit={1}
            value={pendingHorseSelection}
            onSelect={(e, newHorse) => {
              setPendingHorseSelection(newHorse);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenHorsePicker(false);
              setPendingHorseSelection(null);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!pendingHorseSelection || !pendingHorseSelection.horse}
            variant="contained"
            onClick={() => {
              if (!pendingHorseSelection || !pendingHorseSelection.horse) return;
              setSelectedHorseAccess({
                horse: pendingHorseSelection.horse,
                user: tenantUser,
              });
              setOpenHorsePicker(false);
              setPendingHorseSelection(null);
              setOpenAddEditModal(true);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

UserProfileComponent.propTypes = {
  tenant: PropTypes.string,
  tenantUser: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  initialTab: PropTypes.oneOf(['profile', 'emergency', 'files', 'horse-access', 'advanced']),
};
UserProfileComponent.defaultProps = {
  tenant: '',
  tenantUser: null,
  open: false,
  initialTab: 'profile',
  onClose: () => { },
};

export default UserProfileComponent;
